/**
 * PerformancePanel — Dashboard widget:
 *   • Admin: team table with Agent / Calls / Talk Time / Samples / LFB / Achievement %
 *   • Agent: their own day metrics vs daily targets with progress bars
 * Date range filter: today / 7d / 30d / custom.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Target, Trophy, Phone, Loader2 } from "lucide-react";
import { API, useAuth } from "../App";

const fmtTalkTime = (s) => {
  const sec = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
};

const ProgressBar = ({ value, target, color }) => {
  const pct = target > 0 ? Math.min(100, Math.round((value * 100) / target)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium tabular-nums text-slate-900">{value}</span>
        <span className="text-slate-400">/ {target}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[10px] text-slate-500 tabular-nums">{pct}%</p>
    </div>
  );
};

const PerformancePanel = () => {
  const { user } = useAuth();
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ start: today, end: today, preset: "today" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const setPreset = (preset) => {
    const end = new Date();
    const start = new Date();
    if (preset === "7d") start.setDate(start.getDate() - 6);
    else if (preset === "30d") start.setDate(start.getDate() - 29);
    setRange({
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
      preset,
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = isAdmin ? "performance/team" : "performance/me";
      const res = await axios.get(`${API}/${path}`, { params: { start_date: range.start, end_date: range.end } });
      setData(res.data);
    } catch (err) {
      // Non-admin without targets — still shows zero
      console.error("Performance load failed:", err.message);
    } finally { setLoading(false); }
  }, [isAdmin, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const presetBtn = (label, key) => (
    <Button
      size="sm"
      variant={range.preset === key ? "default" : "outline"}
      onClick={() => setPreset(key)}
      className={range.preset === key ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
      data-testid={`perf-preset-${key}`}
    >{label}</Button>
  );

  return (
    <Card className="border-slate-200" data-testid="performance-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            {isAdmin ? "Team Performance vs Targets" : "My Performance vs Targets"}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {presetBtn("Today", "today")}
            {presetBtn("Last 7 days", "7d")}
            {presetBtn("Last 30 days", "30d")}
            <div className="flex items-center gap-1 ml-2">
              <Input
                type="date"
                value={range.start}
                onChange={(e) => setRange({ ...range, start: e.target.value, preset: "custom" })}
                className="h-8 w-36 text-xs"
                data-testid="perf-start-date"
              />
              <span className="text-slate-400">to</span>
              <Input
                type="date"
                value={range.end}
                onChange={(e) => setRange({ ...range, end: e.target.value, preset: "custom" })}
                className="h-8 w-36 text-xs"
                data-testid="perf-end-date"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : isAdmin ? (
          /* ADMIN: team table */
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="team-performance-table">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Agent</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Leads Assigned</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Calls Attempted</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Total Talk Time</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Samples Shared</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">LFB Created</th>
                  <th className="text-center text-xs font-semibold text-slate-600 uppercase py-2.5 px-4">Achievement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.agents?.length ? data.agents.map((a) => (
                  <tr key={a.user_id} className="hover:bg-slate-50" data-testid={`perf-row-${a.user_id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{a.name}</div>
                      <div className="text-xs text-slate-500">{a.email}{a.role === "admin" ? " · Admin" : ""}</div>
                    </td>
                    <td className="py-3 px-4 text-center font-bold tabular-nums text-indigo-700">
                      {a.leads_assigned ?? 0}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="font-semibold tabular-nums text-slate-900">{a.calls_attempted}</div>
                      {a.target.calls > 0 && <div className="text-[11px] text-slate-400 tabular-nums">target {a.target.calls}</div>}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold tabular-nums text-emerald-600">
                      {fmtTalkTime(a.total_talk_time)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="font-semibold tabular-nums text-slate-900">{a.samples_shared}</div>
                      {a.target.samples > 0 && <div className="text-[11px] text-slate-400 tabular-nums">target {a.target.samples}</div>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="font-semibold tabular-nums text-slate-900">{a.lfb_created}</div>
                      {a.target.lfb > 0 && <div className="text-[11px] text-slate-400 tabular-nums">target {a.target.lfb}</div>}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {a.achievement_pct == null ? (
                        <span className="text-xs text-slate-400 italic">no targets set</span>
                      ) : (
                        <span className={`inline-flex items-center justify-center w-14 h-7 rounded-full text-sm font-bold tabular-nums ${
                          a.achievement_pct >= 100 ? "bg-emerald-100 text-emerald-700" :
                          a.achievement_pct >= 75 ? "bg-blue-100 text-blue-700" :
                          a.achievement_pct >= 50 ? "bg-amber-100 text-amber-700" :
                          "bg-rose-100 text-rose-700"
                        }`}>
                          {a.achievement_pct}%
                        </span>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="text-center text-slate-400 py-8">No agents found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* AGENT: personal scorecard */
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-blue-600" />
                <p className="text-xs font-semibold text-blue-700 uppercase">Calls Attempted</p>
              </div>
              {data?.target?.calls > 0 ? (
                <ProgressBar value={data?.metrics?.calls_attempted || 0} target={data.target.calls} color="bg-blue-500" />
              ) : (
                <p className="text-2xl font-bold text-blue-700 tabular-nums">{data?.metrics?.calls_attempted || 0}</p>
              )}
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">Talk Time</p>
              <p className="text-2xl font-bold text-emerald-700 tabular-nums">{fmtTalkTime(data?.metrics?.total_talk_time)}</p>
            </div>
            <div className="bg-violet-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-violet-600" />
                <p className="text-xs font-semibold text-violet-700 uppercase">Samples Shared</p>
              </div>
              {data?.target?.samples > 0 ? (
                <ProgressBar value={data?.metrics?.samples_shared || 0} target={data.target.samples} color="bg-violet-500" />
              ) : (
                <p className="text-2xl font-bold text-violet-700 tabular-nums">{data?.metrics?.samples_shared || 0}</p>
              )}
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase mb-2">LFB Created</p>
              {data?.target?.lfb > 0 ? (
                <ProgressBar value={data?.metrics?.lfb_created || 0} target={data.target.lfb} color="bg-amber-500" />
              ) : (
                <p className="text-2xl font-bold text-amber-700 tabular-nums">{data?.metrics?.lfb_created || 0}</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformancePanel;
